import React from 'react';
import { motion } from 'framer-motion';
import { SITE } from '@/constants/testIds';

const SCHOOLS = ['IIM Ahmedabad', 'HKUST', 'NIT Trichy', 'SP Jain Global'];

/**
 * Credibility statement — light stone section right after hero.
 * A single editorial paragraph + institutional pedigree pills.
 */
export const Credibility = () => {
  const ease = [0.22, 1, 0.36, 1];
  return (
    <section
      id="credibility"
      data-testid={SITE.credibility}
      className="section-light py-24 md:py-32 px-5 md:px-10 relative"
    >
      <div className="container-narrow">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
          <div className="col-span-12 md:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 0.7, ease }}
              className="flex items-center gap-3"
            >
              <span className="index-chip text-[color:var(--gold-3)]">
                01
              </span>
              <span className="eyebrow eyebrow-light">Credibility</span>
            </motion.div>
          </div>

          <div className="col-span-12 md:col-span-9">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 0.9, ease }}
              className="font-serif text-[26px] sm:text-[32px] md:text-[40px] leading-[1.2] tracking-[-0.01em] text-[color:var(--charcoal)] max-w-[900px]"
            >
              Four decades of collective leadership across investment banking,
              M&amp;A execution and strategic advisory — shaped by tier-1
              institutions and applied to founder-led situations that demand
              candor, speed and a partner in the room.
            </motion.p>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-[color:var(--rule-light)]">
              <StatCell
                n="40+"
                label="Years of combined leadership experience"
              />
              <StatCell
                n="03"
                label="Regions actively served"
              />
              <StatCell
                n="04"
                label="Tier-1 institutional alma maters"
              />
              <StatCell
                last
                n="1:1"
                label="Partner-to-mandate engagement"
              />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 0.7, delay: 0.15, ease }}
              className="mt-14"
            >
              <p className="small-caps text-[11px] text-[color:var(--gold-3)]">
                Institutional Pedigree
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {SCHOOLS.map((s) => (
                  <span
                    key={s}
                    className="px-3.5 py-1.5 border border-[color:var(--rule-light-2)] rounded-full text-[12px] tracking-[0.05em] text-[color:var(--charcoal)]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatCell = ({ n, label, last }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-10% 0px' }}
    transition={{ duration: 0.75 }}
    className={`py-6 pr-6 border-b border-[color:var(--rule-light)] md:border-b-0 ${
      last ? '' : 'md:border-r md:border-[color:var(--rule-light)]'
    }`}
  >
    <div className="font-serif text-[42px] md:text-[52px] leading-none text-[color:var(--charcoal)] tracking-[-0.02em]">
      {n}
    </div>
    <div className="mt-3 text-[12.5px] leading-[1.5] text-[color:var(--charcoal-2)] max-w-[190px]">
      {label}
    </div>
  </motion.div>
);

export default Credibility;
